import {PaletteOptions} from '@mui/material'

/**
 * MUI palette aligned to Fundamental's `ThemeLightBlueGrey` token values.
 *
 * Values are hardcoded hex (not `var(--token)`) on purpose: MUI's built-in
 * components and utilities (`alpha()`, `lighten()`, `darken()`) parse palette
 * colours at runtime and cannot handle CSS variable strings. Keep these in sync
 * with the Fundamental `ThemeLightBlueGrey` theme when it changes.
 */
export const fundamentalCssVarPalette: PaletteOptions = {
  mode: 'light',
  primary: {
    main: '#016ad4', // primary
    light: '#80b4e9', // primary_faded_30
    dark: '#0155aa', // primary_selected
    contrastText: '#ffffff' // foreground_inverted
  },
  secondary: {
    main: '#668099', // neutral
    light: '#b2bfcc', // neutral_faded_30
    dark: '#3d4d5c', // neutral_faded_40
    contrastText: '#ffffff'
  },
  error: {
    main: '#da0901', // negative
    light: '#ed8480', // negative_faded_30
    dark: '#ae0701', // negative_selected
    contrastText: '#ffffff'
  },
  warning: {
    main: '#ee8b00', // warning
    light: '#f7c580', // warning_faded_30
    dark: '#be6f00', // warning_selected
    contrastText: '#ffffff'
  },
  success: {
    main: '#00ab50', // positive
    light: '#80d5a7', // positive_faded_30
    dark: '#008940', // positive_selected
    contrastText: '#ffffff'
  },
  info: {
    main: '#016ad4', // info
    light: '#80b4e9', // info_faded_30
    dark: '#0155aa', // info_selected
    contrastText: '#ffffff'
  },
  text: {
    primary: '#29333d', // foreground
    secondary: '#52667a', // foreground_faded
    disabled: '#8599ad' // foreground_faded_20
  },
  background: {
    paper: '#ffffff', // container_background
    default: '#f0f2f5' // page_background
  },
  divider: '#e8ecf0', // border
  borderHighlight: '#d1d9e0', // border_highlight_10
  action: {
    active: '#016ad4', // foreground_interaction
    hover: '#f2f8fd', // container_background_hover
    selected: '#e6f0fb', // container_background_selected
    disabled: '#8599ad', // foreground_faded_20
    disabledBackground: '#e8ecf0' // neutral_faded_20
  }
}

/**
 * MUI palette aligned to Fundamental's `ThemeDarkBlue` token values.
 *
 * Hardcoded hex for the same reason as `fundamentalCssVarPalette`. Keep in sync
 * with the Fundamental `ThemeDarkBlue` theme when it changes.
 */
export const fundamentalDarkPalette: PaletteOptions = {
  mode: 'dark',
  primary: {
    main: '#3488dd', // primary
    light: '#80b4e9', // primary_selected
    dark: '#014a94', // primary_faded_30
    contrastText: '#ffffff'
  },
  secondary: {
    main: '#94a6b8', // neutral
    light: '#b2bfcc', // neutral_selected
    dark: '#5c738a', // neutral_faded_30_selected
    contrastText: '#000000'
  },
  error: {
    main: '#e5534d', // negative
    light: '#ed8480', // negative_selected
    dark: '#c40801', // negative_faded_30_selected
    contrastText: '#ffffff'
  },
  warning: {
    main: '#f3ae4d', // warning
    light: '#f7c580', // warning_selected
    dark: '#d67d00', // warning_faded_30_selected
    contrastText: '#000000'
  },
  success: {
    main: '#4dc484', // positive
    light: '#80d5a7', // positive_selected
    dark: '#009a48', // positive_faded_30_selected
    contrastText: '#000000'
  },
  info: {
    main: '#3488dd', // info
    light: '#80b4e9', // info_selected
    dark: '#015fbf', // info_faded_30_selected
    contrastText: '#ffffff'
  },
  text: {
    primary: '#ffffff', // foreground
    secondary: '#8599ad', // foreground_faded
    disabled: '#52667a' // foreground_faded_20
  },
  background: {
    paper: '#1a2026', // container_background
    default: '#000000' // page_background
  },
  divider: '#242d36', // border
  borderHighlight: '#33404d', // border_highlight_10
  action: {
    active: '#3488dd', // foreground_interaction
    hover: '#1f262e', // container_background_hover
    selected: '#242d36', // container_background_selected
    disabled: '#52667a', // foreground_faded_20
    disabledBackground: '#33404d' // neutral_faded_20
  }
}

/**
 * MUI palette aligned to Fundamental's `ThemeLightBlueContrast` token values.
 *
 * This is a "contrast surface" palette: content sits on the blue primary surface,
 * so foreground tokens are white and containers (menus/popovers) are dark blue.
 * Apply it (via a nested `ThemeProvider`) around regions rendered on the blue
 * header so token-based styles (`text.primary`, `divider`, ...) resolve to the
 * on-blue colours automatically. Hardcoded hex for the same reason as the
 * palettes above. Keep in sync with Fundamental `ThemeLightBlueContrast`.
 */
export const fundamentalLightBlueContrastPalette: PaletteOptions = {
  mode: 'light',
  primary: {
    main: '#016ad4', // primary
    light: '#67a6e5', // primary_faded_30
    dark: '#0155aa', // primary_selected
    contrastText: '#ffffff' // foreground
  },
  secondary: {
    main: '#ffffff99', // neutral
    light: '#ffffffb3', // neutral_selected
    dark: '#ffffff99', // neutral
    contrastText: '#29333d' // foreground_inverted
  },
  error: {
    main: '#da0901', // negative
    light: '#ed8480', // negative_faded_30
    dark: '#ae0701', // negative_selected
    contrastText: '#ffffff'
  },
  warning: {
    main: '#ee8b00', // warning
    light: '#f7c580', // warning_faded_30
    dark: '#be6f00', // warning_selected
    contrastText: '#ffffff'
  },
  success: {
    main: '#00ab50', // positive
    light: '#80d5a7', // positive_faded_30
    dark: '#008940', // positive_selected
    contrastText: '#ffffff'
  },
  info: {
    main: '#016ad4', // info
    light: '#67a6e5', // info_faded_30
    dark: '#0155aa', // info_selected
    contrastText: '#ffffff'
  },
  text: {
    primary: '#ffffff', // foreground
    secondary: '#ffffff99', // foreground_faded
    disabled: '#ffffff40' // foreground_faded_20
  },
  background: {
    paper: '#01407f', // container_background
    default: '#01407f' // page_background_depricated (blue header surface)
  },
  divider: '#ffffff21', // border
  borderHighlight: '#ffffff40', // border_highlight_10
  action: {
    active: '#ffffff', // foreground_interaction
    hover: '#014a94', // container_background_hover
    selected: '#0155aa', // container_background_elevated_selected
    disabled: '#ffffff40', // foreground_faded_20
    disabledBackground: '#ffffff1a' // neutral_faded_20
  }
}

/**
 * Fundamental-specific tokens that have no standard MUI palette slot.
 * Hardcoded to `ThemeLightBlueGrey` values for the same reason as above.
 */
export const fundamentalExtendedPalette = {
  accent1: {
    main: '#8f26e0', // accent_1
    light: '#c792ef', // accent_1_faded_30
    dark: '#561786', // accent_1_faded_40
    faded10: '#f9f4fd', // accent_1_faded_10
    faded20: '#eedefa', // accent_1_faded_20
    toggled: '#390f5a' // accent_1_toggled
  },
  accent2: {
    main: '#009d93', // accent_2
    light: '#80cec9', // accent_2_faded_30
    dark: '#005e58', // accent_2_faded_40
    faded10: '#f2fafa', // accent_2_faded_10
    faded20: '#d9f0ef', // accent_2_faded_20
    toggled: '#003f3b' // accent_2_toggled
  },
  accent3: {
    main: '#cb2bbc', // accent_3
    light: '#db6bd0', // accent_3_faded_30
    dark: '#7a1a71', // accent_3_faded_40
    faded10: '#fcf4fc', // accent_3_faded_10
    faded20: '#f7dff5', // accent_3_faded_20
    toggled: '#51114b' // accent_3_toggled
  },
  accent4: {
    main: '#98c31c', // accent_4
    light: '#cbe18d', // accent_4_faded_30
    dark: '#5b7511', // accent_4_faded_40
    faded10: '#fafcf4', // accent_4_faded_10
    faded20: '#f0f6dd', // accent_4_faded_20
    toggled: '#3d4e0b' // accent_4_toggled
  },
  accent5: {
    main: '#ee6400', // accent_5
    light: '#f7b180', // accent_5_faded_30
    dark: '#8f3c00', // accent_5_faded_40
    faded10: '#fef7f2', // accent_5_faded_10
    faded20: '#fce8d9', // accent_5_faded_20
    toggled: '#5f2800' // accent_5_toggled
  },
  neutral: {
    main: '#668099', // neutral
    faded10: '#f7f9fa', // neutral_faded_10
    faded20: '#e8ecf0', // neutral_faded_20
    faded30: '#b2bfcc', // neutral_faded_30
    faded40: '#3d4d5c', // neutral_faded_40
    toggled: '#29333d' // neutral_toggled
  },
  foreground: {
    main: '#29333d', // foreground
    faded: '#52667a', // foreground_faded
    inverted: '#ffffff', // foreground_inverted
    interaction: '#016ad4' // foreground_interaction
  },
  border: {
    main: '#e8ecf0', // border
    highlight10: '#d1d9e0', // border_highlight_10
    highlight20: '#a3b3c2', // border_highlight_20
    highlight30: '#29333d' // border_highlight_30
  },
  container: {
    background: '#ffffff', // container_background
    backgroundHover: '#f2f8fd', // container_background_hover
    backgroundSelected: '#e6f0fb', // container_background_selected
    backgroundElevated: '#ffffff', // container_background_elevated
    backgroundContrast: '#1f262e' // container_background_contrast
  },
  button: {
    background: '#ffffff', // button_background
    backgroundHover: '#f7f9fa', // button_background_hover
    backgroundSelected: '#f0f2f5', // button_background_selected
    backgroundToggled: '#29333d' // button_background_toggled
  },
  columnHeader: {
    background: '#f0f2f5' // columnheader_background
  },
  progressBar: {
    background: '#e8ecf0' // progressbar_background
  },
  navigation: {
    background: '#ffffff' // navigation_background
  },
  kpi: {
    background: '#ffffff' // kpi_background
  },
  shadow: {
    main: '#00000014', // shadow
    faded: '#0000000a' // shadow_faded
  }
} as const
